import React, { useCallback, useEffect, useState } from 'react';
import type { MarketGoodsCarouselProps } from './MarketGoodsCarouselProps';
import {
  Divider,
  Flex,
  GoodsCarouselSkeleton,
  Headline,
  HeadlineRightPanel,
  IconChevronRight,
  List,
  TextButton,
} from '@banhala/ui-kit';
import { useMarketGoodsCarousels } from '../../hooks/useMarketGoodsCarousels';
import { bridge, isEmpty, isNil, isNotNil, isWeb } from 'shared/src/utils';
import { GoodsCarouselListView } from './components/GoodsCarouselListView';
import { useLogClickMore } from './hooks/useLogClickMore';
import type { FlatListProps } from 'react-native';
import type { MarketHomeComponentItem } from 'shared/src/api';
import { ErrorBoundary } from 'shared/src/component';
import { useMarketSno } from '../../hooks/useMarketSno';

const CarouselDivider = () => <Divider size={'medium'} />;

const MarketGoodsCarousel: React.FC<MarketGoodsCarouselProps> = () => {
  const marketId = useMarketSno();
  const {
    data: goodsCarousel,
    isLoading: isMarketGoodsCarouselsLoading,
    isSuccess: isSuccessMarketGoodsCarouselLoad,
  } = useMarketGoodsCarousels(marketId);
  const logClickMore = useLogClickMore();
  const [isSetOnceViewableOnceFlag, setIsSetOnceViewableOnceFlag] = useState(
    !isSuccessMarketGoodsCarouselLoad,
  );
  const [viewableOnce, setViewableOnce] = useState<{
    [listId: string]: boolean;
  }>(
    isSuccessMarketGoodsCarouselLoad
      ? (goodsCarousel?.components.reduce<{ [listId: string]: boolean }>(
          (acc, component) => {
            return { ...acc, [component.list_id]: isWeb };
          },
          {},
        ) ?? {})
      : {},
  );

  useEffect(() => {
    if (isSuccessMarketGoodsCarouselLoad && !isSetOnceViewableOnceFlag) {
      // use-query를 사용 하기 때문에 해당 데이터는 없을수도 있음
      // 이를 해결하기 위해서 goodsCarousel이 null이 아니고, viewableOnce
      if (isNotNil(goodsCarousel) && Object.keys(viewableOnce).length === 0) {
        //설정된 값이 없는 경우라면,
        setViewableOnce(
          goodsCarousel?.components.reduce<{ [listId: string]: boolean }>(
            (acc, component) => {
              return { ...acc, [component.list_id]: isWeb };
            },
            {},
          ),
        );
      }
      setIsSetOnceViewableOnceFlag(true);
    }
  }, [
    goodsCarousel,
    isSetOnceViewableOnceFlag,
    isSuccessMarketGoodsCarouselLoad,
    viewableOnce,
  ]);

  const onViewableItemsChanged = useCallback<
    NonNullable<
      FlatListProps<MarketHomeComponentItem>['onViewableItemsChanged']
    >
  >(({ viewableItems }) => {
    setViewableOnce(prev => ({
      ...prev,
      ...viewableItems.reduce((acc, cur) => {
        return {
          ...acc,
          [cur.item.list_id]: cur.isViewable || prev[cur.item.list_id],
        };
      }, {}),
    }));
  }, []);

  if (isMarketGoodsCarouselsLoading) {
    return <></>;
  }

  if (isNil(goodsCarousel) || isEmpty(goodsCarousel.components)) {
    return null;
  }

  return (
    <List
      data={goodsCarousel.components}
      ListFooterComponent={CarouselDivider}
      ItemSeparatorComponent={CarouselDivider}
      onViewableItemsChanged={onViewableItemsChanged}
      renderItem={({ item: component }) => {
        return (
          <Flex>
            <Headline
              size="medium"
              rightPanel={
                isNotNil(component.entity.deep_link) ? (
                  <HeadlineRightPanel type="textButton">
                    <TextButton
                      color="blue"
                      onPress={() => {
                        if (isNotNil(component.entity.deep_link)) {
                          logClickMore(component.list_id);

                          bridge.openDeeplink(component.entity.deep_link);
                        }
                      }}
                      size="small"
                      icon={<IconChevronRight />}
                      testID={`${component.list_id}_전체보기버튼`}
                    >
                      전체보기
                    </TextButton>
                  </HeadlineRightPanel>
                ) : undefined
              }
              headline={component.entity.header.title}
            />
            <React.Suspense
              fallback={
                <GoodsCarouselSkeleton count={4}></GoodsCarouselSkeleton>
              }
            >
              {viewableOnce[component.list_id] ? (
                <GoodsCarouselListView
                  list={component.entity.item_list}
                  listId={component.list_id}
                  marketId={marketId}
                />
              ) : (
                <GoodsCarouselSkeleton count={3} />
              )}
            </React.Suspense>
          </Flex>
        );
      }}
    />
  );
};

const MarketGoodsCarouselWrapper = () => {
  return (
    // 에러 시 캐러셀 안 보여주기
    <ErrorBoundary ErrorFallback={() => <></>}>
      <MarketGoodsCarousel />
    </ErrorBoundary>
  );
};

export default MarketGoodsCarouselWrapper;
